import React, {useState, useMemo, memo} from 'react';
import {Props} from './ac-rating-input.proptypes';
import clsx from 'clsx';

// Import => Constants
import {ICONS} from '@asdf-constants';

// Import => Utilities
import {AcUUID, AcIsFunction, AcIsSet} from '@asdf-utils';

// Import => Atoms
import {AcIcon} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-rating-input',
  LABEL: 'ac-rating-input__label',
  WRP: 'ac-rating-input__wrp',
  STAR: {
    MAIN: 'ac-rating-input__star',
    ACTIVE: 'ac-rating-input__star--active',
    SYMBOL: 'ac-rating-input__star__symbol',
    MASK: 'ac-rating-input__star__mask',
  },
};

const AcRatingInput = memo(
  ({
    debug = false,
    id = AcUUID(),
    label = 'Rating input',
    name = 'rating_input',
    value = 0,
    stars = 5,
    fractions = 1,
    callback = () => {},
    type = 'rating',
    className,
    ...rest
  }) => {
    const [interactive, setInteractive] = useState(false);
    const [rating, setRating] = useState(value || 0);
    const [displayRating, setDisplayRating] = useState(value || 0);

    const isTouchEnabled = () => {
      return (
        'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
      );
    };

    const handleClick = (event, starIndex) => {
      if (event && event.persist) event.persist();
      if (event && event.preventDefault && event.cancelable) event.preventDefault();

      const ratingValue = calculateDisplayValue(starIndex, event);

      if (!AcIsSet(ratingValue)) return;

      setDisplayRating(ratingValue);
      setRating(ratingValue);
      setInteractive(false);

      if (callback && AcIsFunction(callback)) {
        callback(event, name, ratingValue, type);
      }
    };

    const handleMouseMove = (event, starIndex) => {
      if (event && event.persist) event.persist();
      if (event && event.preventDefault && event.cancelable) event.preventDefault();

      const ratingValue = calculateDisplayValue(starIndex, event);

      if (!AcIsSet(ratingValue)) return;

      setDisplayRating(ratingValue);
      setInteractive(true);
    };

    const handleMouseLeave = (event, starIndex) => {
      if (event && event.persist) event.persist();
      if (event && event.preventDefault && event.cancelable) event.preventDefault();

      setDisplayRating(rating);
      setInteractive(false);
    };

    const handleTouchEnd = (event, starIndex) => {
      if (event && event.persist) event.persist();
      if (event && event.preventDefault && event.cancelable) event.preventDefault();

      if (!AcIsSet(starIndex)) return;

      setDisplayRating(starIndex);
      setRating(starIndex);
      setInteractive(false);

      if (callback && AcIsFunction(callback)) {
        callback(event, name, starIndex, type);
      }
    };

    const calculateHoverPercentage = (event, starIndex) => {
      let clientX = 0;

      if (event.nativeEvent.type.indexOf('touch') > -1) {
        if (event.nativeEvent.type.indexOf('touchend') > -1) {
          clientX = event.changedTouches[0].clientX;
        } else {
          clientX = event.touches[0].clientX;
        }
      } else {
        clientX = event.clientX;
      }

      const targetRect = event.target.getBoundingClientRect();
      const delta = clientX - targetRect.left;

      return delta < 0 ? 0 : delta / targetRect.width;
    };

    const calculateDisplayValue = (starIndex, event) => {
      const percentage = calculateHoverPercentage(event);
      const fraction = Math.ceil((percentage % 1) * fractions) / fractions;
      const precision = 10 ** 3;
      const displayValue =
        starIndex + (Math.floor(percentage) + Math.floor(fraction * precision) / precision);

      return displayValue > 0 ? (displayValue > stars ? stars : displayValue) : 1 / fractions;
    };

    const getStarMaskClassNames = useMemo(() => {
      return clsx(_CLASSES.STAR.MASK);
    }, [stars]);

    const getStarSymbolClassNames = useMemo(() => {
      return clsx(_CLASSES.STAR.SYMBOL);
    }, [stars]);

    const getStarClassNames = useMemo(() => {
      return clsx(_CLASSES.STAR.MAIN);
    }, [stars]);

    const getStarWrpClassNames = useMemo(() => {
      return clsx(_CLASSES.WRP);
    }, []);

    const getLabelClassNames = useMemo(() => {
      return clsx(_CLASSES.LABEL);
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx(_CLASSES.MAIN, className);
    }, [className]);

    const renderStars = useMemo(() => {
      const len = stars;
      let n = 0;
      let result = [];

      let percentage;
      const renderedValue = interactive ? displayRating : rating;
      const totalSymbols = Math.floor(renderedValue);

      for (n; n < len; n++) {
        const starIndex = n;
        const num = starIndex + 1;
        const plural = num !== 1 && num !== 0.5;

        // Calculate each symbol's fullness percentage
        if (starIndex - totalSymbols < 0) {
          percentage = 100;
        } else if (starIndex - totalSymbols === 0) {
          percentage = (renderedValue - starIndex) * 100;
        } else {
          percentage = 0;
        }

        let events = {};

        if (isTouchEnabled()) {
          events = {
            onTouchMove: event => {
              handleMouseMove(event, starIndex + 1);
            },
            onTouchEnd: event => {
              handleTouchEnd(event, starIndex + 1);
            },
          };
        } else {
          events = {
            onClick: event => {
              handleClick(event, starIndex);
            },
            onMouseMove: event => {
              handleMouseMove(event, starIndex);
            },
          };
        }

        const object = (
          <div
            className={getStarClassNames}
            key={`ac-rating-input--${id}-${starIndex}`}
            index={starIndex}
            {...events}
          >
            <div className={getStarSymbolClassNames}>
              <AcIcon icon={ICONS.STAR_OUTLINE} title={`${num} star${plural ? 's' : ''}`} />
            </div>
            <div className={getStarMaskClassNames} style={{width: `${percentage}%`}}>
              <AcIcon icon={ICONS.STAR} title={`${num} star${plural ? 's' : ''}`} />
            </div>
          </div>
        );

        result.push(object);
      }

      return result;
    }, [stars, rating, displayRating, interactive]);

    return (
      <div className={getMainClassNames} {...rest}>
        <div
          className={getLabelClassNames}
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />

        <div className={getStarWrpClassNames} onMouseLeave={handleMouseLeave}>
          {renderStars}
        </div>

        {debug && (
          <>
            <p>Rating: {rating}</p>
            <p>displayRating: {displayRating}</p>
          </>
        )}
      </div>
    );
  }
);

AcRatingInput.displayName = 'AcRatingInput';
AcRatingInput.propTypes = Props;

export {AcRatingInput};
export default AcRatingInput;
